import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Check, Crown, Sparkles, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import axios from "axios";
import confetti from "canvas-confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("processing");
  const [attempts, setAttempts] = useState(0);
  const paymentMethod = searchParams.get("method") || "stripe";

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const checkoutId = searchParams.get("checkout_id");
    
    // GoCardless flow
    if (paymentMethod === "gocardless" && checkoutId) {
      const checkGoCardlessStatus = async () => {
        try {
          const response = await axios.get(`${API}/payments/gocardless/status/${checkoutId}`);
          
          if (response.data.status === "subscription_active" || response.data.status === "mandate_created") {
            setStatus("success");
            await refreshUser();
            
            // Celebrate!
            confetti({
              particleCount: 100,
              spread: 70,
              origin: { y: 0.6 },
              colors: ['#14B8A6', '#06B6D4', '#F472B6', '#A78BFA']
            });
          } else if (response.data.status === "awaiting_authorisation" && attempts < 10) {
            // Keep polling - mandate takes a moment to activate
            setTimeout(() => setAttempts(a => a + 1), 3000);
          } else if (attempts >= 10) {
            // After 30 seconds, show pending message
            setStatus("pending");
          }
        } catch (error) {
          if (attempts < 5) {
            setTimeout(() => setAttempts(a => a + 1), 3000);
          } else {
            setStatus("pending");
          }
        }
      };
      
      checkGoCardlessStatus();
      return;
    }
    
    // Stripe flow
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/subscription/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          await refreshUser();
          
          // Celebrate!
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#0EA5E9', '#F472B6', '#FCD34D', '#A78BFA']
          });
        } else if (response.data.status === "expired") {
          setStatus("error");
        } else if (attempts < 5) {
          // Keep polling
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("error");
        }
      } catch (error) {
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkStatus();
  }, [searchParams, attempts, refreshUser, paymentMethod]);

  return (
    <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center px-6" data-testid="subscription-success">
      {/* Decorative blobs */}
      <div className="blob blob-yellow w-80 h-80 -top-40 -right-40 fixed opacity-40" />
      <div className="blob blob-pink w-64 h-64 bottom-20 -left-32 fixed opacity-40" />

      <div className="relative z-10 max-w-md w-full text-center">
        {status === "processing" && (
          <>
            <div className="w-24 h-24 rounded-full premium-gradient flex items-center justify-center mx-auto mb-6 animate-pulse-soft">
              <Crown className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Processing...
            </h1>
            <p className="text-slate-600 mb-6">
              Please wait while we confirm your payment
            </p>
            <div className="w-48 h-2 bg-slate-200 rounded-full mx-auto overflow-hidden">
              <div className="h-full premium-gradient rounded-full animate-pulse" style={{ width: `${(attempts + 1) * 20}%` }} />
            </div>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-6 animate-fade-in">
              <Check className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4 animate-fade-in" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Welcome to Premium!
            </h1>
            <p className="text-slate-600 mb-8 animate-fade-in">
              Thank you for upgrading! You now have access to all premium features.
            </p>
            
            <div className="bg-white rounded-3xl p-6 shadow-lg mb-8 animate-fade-in-up">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-amber-500" />
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>What&apos;s Unlocked</h2>
              </div>
              <ul className="space-y-3 text-left">
                {[
                  "Unlimited matches every day",
                  "See all profile photos",
                  "Unlimited messaging",
                  "Playdate planner access",
                  "AI activity suggestions"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center">
                      <Check className="w-4 h-4 text-teal-500" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Link to="/discover">
              <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6 text-lg" data-testid="start-discovering-btn">
                <Heart className="w-5 h-5 mr-2" />
                Start Discovering Families
              </Button>
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">☹️</span>
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Something went wrong
            </h1>
            <p className="text-slate-600 mb-8">
              We couldn&apos;t confirm your payment. Please try again or contact support.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/subscription">
                <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full" data-testid="try-again-btn">
                  Try Again
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="rounded-full" data-testid="back-dashboard-btn">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center mx-auto mb-6">
              <Building className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Direct Debit Set Up!
            </h1>
            <p className="text-slate-600 mb-4">
              Your Direct Debit mandate is being processed. This usually takes 1-2 business days.
            </p>
            <p className="text-sm text-purple-600 mb-8">
              You&apos;ll receive an email from GoCardless confirming your mandate. Once active, your Premium subscription will start automatically!
            </p>
            
            <div className="bg-teal-50 rounded-2xl p-4 mb-8 border border-teal-100">
              <p className="text-sm text-teal-700">
                <strong>What happens next?</strong><br/>
                • GoCardless will confirm your bank details<br/>
                • Your first payment will be collected automatically<br/>
                • You&apos;ll get Premium access once payment clears
              </p>
            </div>

            <Link to="/dashboard">
              <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6" data-testid="back-dashboard-btn">
                Back to Dashboard
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;